import * as React from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";

import GamesList from "../leagueSeason/GamesList";
import { onSelectGame, onSelectGameMargin, onSelectGameMarginInProgress, completionMessage, elimWeekStatus } from "../leagueSeason/LeagueSeason";

import MessagePanel from "../../controls/MessagePanel";
import LoaderGif from "../../controls/LoaderGif";

import { DataService } from "../../../services/dataService";
import { oldLeagueCutOffId, UtilService } from "../../../services/utilService";

import { IState, ILeagueSeasonState, IUserState, IAppSettings } from "../../../state/state";
import { IAction, toggleGamePickDetail, setLeagueSeasonState } from "../../../actions";
import { IWeek, IUserLeagueView, ITeamInfo, ISeason } from "../../../models/interfaces";

export interface ITeamSeasonProps {
    dispatch: (action: IAction) => void,
    authenticationToken: string,
    leagueSeasonState: ILeagueSeasonState,
    userState: IUserState,
    appSettings: IAppSettings | null,
    allSeasons: Array<ISeason> | null,
    teams: Array<ITeamInfo> | null
}

// leagues for one season. the current season uses the cutoff id (so it behaves
// before appSettings loads); previous seasons match on seasonId.
const userLeagues = (props: ITeamSeasonProps, seasonId?: number): Array<IUserLeagueView> => {
    if (!props.userState || !props.userState.leagues) {
        return [];
    }
    const currentSeasonId = props.appSettings?.currentSeasonId ?? 0;
    const cutOffId = props.appSettings?.oldLeagueCutOffId ?? oldLeagueCutOffId;
    const isCurrentSeason = !seasonId || currentSeasonId === 0 || seasonId === currentSeasonId;
    return _.sortBy(
        props.userState.leagues.filter(l => isCurrentSeason ? l.leagueId >= cutOffId : l.seasonId === seasonId),
        [(l: IUserLeagueView) => l.leagueName.toLowerCase(), (l: IUserLeagueView) => l.idx]);
}

// by default, the first league by alpha that is not an elimination league.
export const defaultTeamViewLeague = (props: ITeamSeasonProps, seasonId?: number): IUserLeagueView | null => {
    const leagues = userLeagues(props, seasonId);
    if (leagues.length === 0) {
        return null;
    }
    return leagues.find(l => !l.elimination) ?? leagues[0];
}

// every season, newest first. years with no leagues for the user get the read-only view.
const availableSeasons = (props: ITeamSeasonProps): Array<ISeason> => {
    return _.orderBy(props.allSeasons ?? [], s => s.seasonId, 'desc');
}

const TeamSeason = (props: ITeamSeasonProps) => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const teamId = parseInt(searchParams.get('teamId') ?? '0', 10);
    const leagueId = parseInt(searchParams.get('leagueId') ?? '0', 10);
    const leagueMemberId = parseInt(searchParams.get('leagueMemberId') ?? '0', 10);
    const urlSeasonId = parseInt(searchParams.get('seasonId') ?? '0', 10);

    // read-only mode: the user has no leagues for the selected year, so games are shown with no pick interactivity.
    const readOnly: boolean = leagueId === 0;

    const requestedReadOnlyRef = React.useRef<string>('');

    // no league in the url yet - resolve the default league for the selected year and redirect,
    // or load the read-only view when the user has no leagues that year.
    React.useEffect(() => {
        if (teamId > 0 && leagueId === 0 && props.userState) {
            const league = defaultTeamViewLeague(props, urlSeasonId > 0 ? urlSeasonId : undefined);
            if (league) {
                navigate(`/teamSeason?teamId=${teamId}&seasonId=${league.seasonId}&leagueId=${league.leagueId}&leagueMemberId=${league.leagueMemberId}`, { replace: true });
            } else {
                const readOnlyKey = `${teamId}:${urlSeasonId}`;
                if (requestedReadOnlyRef.current !== readOnlyKey
                    || (props.leagueSeasonState?.leagueView && props.leagueSeasonState.leagueView.leagueId !== 0)) {
                    // fetch unless a read-only load for this team+year is already in flight or done
                    // (state holding another league means we came back from a league view).
                    requestedReadOnlyRef.current = readOnlyKey;
                    props.dispatch(setLeagueSeasonState(null));
                    DataService.getTeamSeasonState(props.authenticationToken, 0, 0, teamId, urlSeasonId, props.dispatch);
                }
            }
        }
    });

    const team: ITeamInfo | undefined = _.find(props.teams ?? [], t => t.teamId === teamId);

    // the selected year follows the selected league, then the url, then the current season.
    const currentSeasonId = props.appSettings?.currentSeasonId ?? 0;
    const selectedLeague = _.find(props.userState?.leagues ?? [], l => l.leagueId === leagueId && l.leagueMemberId === leagueMemberId);
    const selectedSeasonId = selectedLeague ? selectedLeague.seasonId : (urlSeasonId > 0 ? urlSeasonId : currentSeasonId);

    const teamSelector = (): JSX.Element | null => {
        const teams = _.sortBy(props.teams ?? [], t => t.displayName.toLowerCase());
        if (teams.length === 0) {
            return null;
        }
        return (
            <div className="form-inline">
                <label htmlFor="teamViewTeamSelect">Team:&nbsp;</label>
                <select
                    id="teamViewTeamSelect"
                    className="form-control"
                    value={teamId}
                    onChange={(e) => {
                        const newTeamId = parseInt(e.target.value, 10);
                        // keep the selected year and league.
                        navigate(leagueId > 0
                            ? `/teamSeason?teamId=${newTeamId}&seasonId=${selectedSeasonId}&leagueId=${leagueId}&leagueMemberId=${leagueMemberId}`
                            : `/teamSeason?teamId=${newTeamId}&seasonId=${urlSeasonId}`);
                    }}
                    >
                    {teams.map(t =>
                        <option key={`teamViewTeam${t.teamId}`} value={t.teamId}>{t.displayName}</option>
                    )}
                </select>
            </div>
        );
    }

    const seasonSelector = (): JSX.Element | null => {
        const seasons = availableSeasons(props);
        if (seasons.length === 0) {
            return null;
        }
        return (
            <div className="form-inline">
                <label htmlFor="teamViewSeasonSelect">Year:&nbsp;</label>
                <select
                    id="teamViewSeasonSelect"
                    className="form-control"
                    value={selectedSeasonId}
                    onChange={(e) => {
                        const newSeasonId = parseInt(e.target.value, 10);
                        const league = defaultTeamViewLeague(props, newSeasonId);
                        if (league) {
                            navigate(`/teamSeason?teamId=${teamId}&seasonId=${newSeasonId}&leagueId=${league.leagueId}&leagueMemberId=${league.leagueMemberId}`);
                        } else {
                            // no leagues that year - read-only view of that season.
                            navigate(`/teamSeason?teamId=${teamId}&seasonId=${newSeasonId}`);
                        }
                    }}
                    >
                    {seasons.map(s =>
                        <option key={`teamViewSeason${s.seasonId}`} value={s.seasonId}>{s.name}</option>
                    )}
                </select>
            </div>
        );
    }

    const leagueSelector = (): JSX.Element | null => {
        const leagues = userLeagues(props, selectedSeasonId);
        if (leagues.length === 0) {
            return readOnly
                ? <p>You are not in any leagues for this year, so this view is read-only.</p>
                : null;
        }
        return (
            <div className="form-inline">
                <label htmlFor="teamViewLeagueSelect">League:&nbsp;</label>
                <select
                    id="teamViewLeagueSelect"
                    className="form-control"
                    value={`${leagueId}:${leagueMemberId}`}
                    onChange={(e) => {
                        const [newLeagueId, newLeagueMemberId] = e.target.value.split(':');
                        navigate(`/teamSeason?teamId=${teamId}&seasonId=${selectedSeasonId}&leagueId=${newLeagueId}&leagueMemberId=${newLeagueMemberId}`);
                    }}
                    >
                    {leagues.map(l =>
                        <option key={`teamViewLeague${l.leagueMemberId}`} value={`${l.leagueId}:${l.leagueMemberId}`}>
                            {UtilService.indexedLeagueName(l.leagueName, l.teamName, l.idx)}
                        </option>
                    )}
                </select>
            </div>
        );
    }

    if (teamId === 0) {
        return (
            <div>
                <h2 className="tight-panel">Team View</h2>
                <div className="content-panel">
                    <MessagePanel className="bg-danger" message="Team not found." />
                </div>
            </div>
        );
    }

    if (props.leagueSeasonState?.initErrorMessage && props.leagueSeasonState.initErrorMessage.length > 0) {
        return (
            <div>
                <h2 className="tight-panel">{team ? team.displayName : "Team View"}</h2>
                <div className="content-panel">
                    <div className="selector-row">
                        {teamSelector()}
                        {seasonSelector()}
                        {leagueSelector()}
                    </div>
                    <MessagePanel className="bg-danger" message={props.leagueSeasonState.initErrorMessage} />
                </div>
            </div>
        );
    }

    const loaded =
        _.isObject(props.leagueSeasonState)
        && props.leagueSeasonState.leagueView?.leagueId === leagueId
        && (leagueMemberId === 0 || props.leagueSeasonState.leagueMember?.leagueMemberId === leagueMemberId)
        && (!readOnly || urlSeasonId === 0 || props.leagueSeasonState.leagueView?.seasonId === urlSeasonId);

    if (!loaded) {
        return (
            <div>
                <h2 className="tight-panel">{team ? team.displayName : "Team View"}</h2>
                <div className="content-panel">
                    <div className="selector-row">
                        {teamSelector()}
                        {seasonSelector()}
                        {leagueSelector()}
                    </div>
                    <LoaderGif gif='squares' visible={true} />
                </div>
            </div>
        );
    }

    const onToggleGameDetail = (weekId: number, gameId: number): void => {

        let week: IWeek | undefined = _.find(props.leagueSeasonState.weeks, w => w.weekId === weekId);

        if (!week) {
            return;
        }

        let gamePick = _.find(week.gamePicks, gp => gp.gameId === gameId);

        if (!gamePick) {
            return;
        }

        if (readOnly) {
            // no league context - no pick detail.
            return;
        }

        if (!gamePick.detailVisible) {
            // load it up
            if (props.leagueSeasonState.leagueView) {
                DataService.getGamePickDetail(
                    props.authenticationToken,
                    props.leagueSeasonState.leagueView.leagueId,
                    week.weekId,
                    gamePick.gameId,
                    props.dispatch);
            }
        }

        props.dispatch(toggleGamePickDetail(weekId, gameId));
    }

    const weekMapper = props.leagueSeasonState.weeks.map(
        (week: IWeek): JSX.Element | null => {

            if (!props.leagueSeasonState.leagueRuleSet || !props.leagueSeasonState.leagueView || !props.leagueSeasonState.leagueMemberCurrentWeekPicksDue) {
                return null;
            }

            const minWeekPicks = props.leagueSeasonState.leagueRuleSet.minWeekPicks;

            const className = week.pickCount === minWeekPicks ? "fa fa-check-square-o" : "fa fa-square-o";

            const loader: JSX.Element = (<div className="float-right"><LoaderGif gif='squares' visible={week.loading} /></div>);

            const pickDisplay: string = !_.isUndefined(week.elimPickName) && week.elimPickName !== null && week.elimPickName.length > 0 ? `[${week.elimPickName}] ` : "";

            const header: JSX.Element = readOnly
                ? <div>{week.name}</div>
                : props.leagueSeasonState.leagueRuleSet.elimination
                    ? <div><i className={className} aria-hidden="true"/>&nbsp;{week.name}&nbsp;{pickDisplay}{elimWeekStatus(props.leagueSeasonState, week)}{loader}</div>
                    : <div>{week.name}&nbsp;({week.pickCount.toString()}/{Math.min(week.gameCount, props.leagueSeasonState.leagueRuleSet.maxWeekPicks).toString()}) Points: {week.points}{loader}</div>;

            // only this team's games; week game picks may hold the full week after a refresh.
            const teamGamePicks = week.gamePicks.filter(gp => gp.awayTeamId === teamId || gp.homeTeamId === teamId);

            // regular season weeks with no game are byes; playoff weeks (19+) just have no game scheduled.
            const noGameText: string = week.weekNumber > 18 ? "NO GAME" : "BYE";

            return (
                <div key={`teamWeeksList${week.weekId}`}>
                    <div className="league-box blue-box big-text">
                        {header}
                    </div>
                    {teamGamePicks.length === 0
                        ? <div className="container-fluid game-box">
                            <div className="row no-pad">
                                <div className="col-xs-12 text-center big-text">{noGameText}</div>
                            </div>
                        </div>
                        : <GamesList
                            league={props.leagueSeasonState.leagueView}
                            gamePicks={teamGamePicks}
                            currentWeekId={props.leagueSeasonState.leagueMemberCurrentWeekPicksDue.weekId}
                            currentEliminationPicks={props.leagueSeasonState.currentEliminationPicks}
                            toggleGameDetail={onToggleGameDetail}
                            updateGameSelection={(weekId: number, gameId: number, selectedTeamId: number) => onSelectGame(props, weekId, gameId, selectedTeamId)}
                            updateMarginSelection={(weekId: number, gameId: number, margin: number) => onSelectGameMargin(props, weekId, gameId, margin)}
                            updateMarginSelectionInProgress={(weekId: number, gameId: number, margin: number) => onSelectGameMarginInProgress(props, weekId, gameId, margin)}
                            />}
                </div>
            );
        }
    );

    return (
        <div>
            <h2 className="tight-panel"><img src="/img/new/leagues-32x32.png" className="icon32" alt=""/> {team ? team.displayName : "Team View"} - {props.leagueSeasonState.leagueView?.seasonName}</h2>
            <div className="content-panel">
                <div className="selector-row">
                    {teamSelector()}
                    {seasonSelector()}
                    {leagueSelector()}
                </div>
                {completionMessage(props)}
            </div>
            {weekMapper}
        </div>
    );
}

function mapStateToProps(state: IState) {
    return {
        leagueSeasonState: state.leagueSeasonState,
        userState: state.userState,
        authenticationToken: state.authenticationToken,
        appSettings: state.appSettings,
        allSeasons: state.allSeasons,
        teams: state.teams
    };
}

export default connect(mapStateToProps)(TeamSeason as any);
